import React, { useState, useEffect } from 'react';
import {
    BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
    XAxis, YAxis, CartesianGrid, Tooltip, Legend,
    ResponsiveContainer, AreaChart, Area,
    RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis
} from 'recharts';
import {
    BarChart as BarChartIcon, Users, Zap, Clock, Globe,
    Download, Filter, Calendar, Search, ArrowUp,
    ArrowDown, TrendingUp, PieChart as PieChartIcon,
    Map, Building, Code, Database, Server,
    Cpu, DollarSign, Eye, UserCheck, Smartphone,
    Cloud, Shield, Activity, AlertCircle,
    ChevronDown, ChevronUp, RefreshCw, Settings,
    FileText, ExternalLink, Mail, Phone, DownloadCloud,
    Sun, Moon, X, Menu, Info, Check
} from 'lucide-react';
import './INSDAPIPortal.css';

function Dashboard() {
    const [darkMode, setDarkMode] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [timeRange, setTimeRange] = useState('7d');
    const [activeTab, setActiveTab] = useState('overview');
    const [loading, setLoading] = useState(false);
    const [selectedRegion, setSelectedRegion] = useState(null);

    useEffect(() => {
        if (darkMode) {
            document.documentElement.classList.add('dark');
        } else {
            document.documentElement.classList.remove('dark');
        }
    }, [darkMode]);

    // Données simulées pour les statistiques
    const overviewStats = [
        { label: 'Requêtes totales', value: '2.4M', change: '+12%', icon: Zap, color: '#3b82f6' },
        { label: 'Utilisateurs actifs', value: '1,247', change: '+8%', icon: Users, color: '#10b981' },
        { label: 'APIs les plus utilisées', value: '28', change: '+3', icon: Code, color: '#8b5cf6' },
        { label: 'Taux de succès', value: '99.8%', change: '+0.2%', icon: Shield, color: '#f59e0b' },
        { label: 'Latence moyenne', value: '142ms', change: '-18ms', icon: Clock, color: '#ef4444' },
        { label: 'Données téléchargées', value: '4.2TB', change: '+24%', icon: Download, color: '#06b6d4' }
    ];

    // Données de requêtes par API
    const apiUsageData = [
        { name: 'Démographie', value: 420000, color: '#3b82f6' },
        { name: 'SIRENE BF', value: 380000, color: '#8b5cf6' },
        { name: 'BDM', value: 320000, color: '#10b981' },
        { name: 'Agriculture', value: 280000, color: '#f59e0b' },
        { name: 'Économie', value: 240000, color: '#ef4444' },
        { name: 'Local Data', value: 190000, color: '#06b6d4' },
        { name: 'Santé', value: 150000, color: '#ec4899' },
        { name: 'Autres', value: 420000, color: '#94a3b8' }
    ];

    // Données temporelles des requêtes
    const timeSeriesData = {
        '7d': [
            { date: 'Lun', requests: 85000, errors: 120, latency: 145 },
            { date: 'Mar', requests: 92000, errors: 98, latency: 142 },
            { date: 'Mer', requests: 98000, errors: 110, latency: 138 },
            { date: 'Jeu', requests: 105000, errors: 132, latency: 140 },
            { date: 'Ven', requests: 112000, errors: 145, latency: 148 },
            { date: 'Sam', requests: 76000, errors: 89, latency: 135 },
            { date: 'Dim', requests: 68000, errors: 76, latency: 130 }
        ],
        '30d': Array.from({ length: 30 }, (_, i) => ({
            date: `${i + 1}/01`,
            requests: Math.floor(Math.random() * 50000) + 70000,
            errors: Math.floor(Math.random() * 200) + 50,
            latency: Math.floor(Math.random() * 50) + 120
        })),
        '90d': Array.from({ length: 90 }, (_, i) => ({
            date: `${Math.floor(i / 30) + 1}/${(i % 30) + 1}`,
            requests: Math.floor(Math.random() * 30000) + 60000,
            errors: Math.floor(Math.random() * 150) + 30,
            latency: Math.floor(Math.random() * 40) + 110
        }))
    };

    // Top utilisateurs
    const topUsers = [
        { rank: 1, name: 'Université de Ouaga', requests: 245000, apis: 12, location: 'Ouagadougou' },
        { rank: 2, name: 'Ministère Économie', requests: 198000, apis: 8, location: 'Ouagadougou' },
        { rank: 3, name: 'AgroTech BF', requests: 156000, apis: 4, location: 'Bobo-Dioulasso' },
        { rank: 4, name: 'Banque Centrale', requests: 142000, apis: 6, location: 'Ouagadougou' },
        { rank: 5, name: 'ONG Santé Plus', requests: 128000, apis: 3, location: 'Koudougou' },
        { rank: 6, name: 'Startup DataLab', requests: 112000, apis: 9, location: 'Ouagadougou' },
        { rank: 7, name: 'Mairie Ouaga', requests: 98000, apis: 5, location: 'Ouagadougou' },
        { rank: 8, name: 'Chercheur Indépendant', requests: 85000, apis: 7, location: 'Paris, FR' }
    ];

    // Données géographiques
    const geographicData = [
        { region: 'Centre', requests: 850000, users: 420, color: '#3b82f6' },
        { region: 'Hauts-Bassins', requests: 320000, users: 156, color: '#10b981' },
        { region: 'Cascades', requests: 185000, users: 89, color: '#f59e0b' },
        { region: 'Plateau Central', requests: 142000, users: 67, color: '#ef4444' },
        { region: 'Nord', requests: 98000, users: 45, color: '#8b5cf6' },
        { region: 'Étranger', requests: 420000, users: 470, color: '#06b6d4' }
    ];

    // Types d'applications
    const appTypesData = [
        { type: 'Applications web', value: 45, color: '#3b82f6' },
        { type: 'Mobile', value: 30, color: '#10b981' },
        { type: 'Scripts/CLI', value: 15, color: '#f59e0b' },
        { type: 'BI/Analytics', value: 8, color: '#8b5cf6' },
        { type: 'Automatisation', value: 2, color: '#ef4444' }
    ];

    // Tendance d'adoption
    const adoptionData = [
        { month: 'Jan', newUsers: 42, activeUsers: 856 },
        { month: 'Fév', newUsers: 56, activeUsers: 912 },
        { month: 'Mar', newUsers: 48, activeUsers: 960 },
        { month: 'Avr', newUsers: 62, activeUsers: 1022 },
        { month: 'Mai', newUsers: 71, activeUsers: 1093 },
        { month: 'Jun', newUsers: 85, activeUsers: 1178 },
        { month: 'Jul', newUsers: 92, activeUsers: 1270 },
        { month: 'Aoû', newUsers: 78, activeUsers: 1348 },
        { month: 'Sep', newUsers: 89, activeUsers: 1437 },
        { month: 'Oct', newUsers: 104, activeUsers: 1541 },
        { month: 'Nov', newUsers: 112, activeUsers: 1653 },
        { month: 'Déc', newUsers: 125, activeUsers: 1778 }
    ];

    // Alertes et notifications
    const alerts = [
        { type: 'warning', message: 'Latence élevée sur API Démographie', time: '2h', priority: 'high' },
        { type: 'info', message: 'Nouveau record de requêtes journalières', time: '1j', priority: 'medium' },
        { type: 'success', message: 'Migration ODP 2.0 complétée', time: '3j', priority: 'low' },
        { type: 'warning', message: 'API Santé en maintenance samedi', time: '5j', priority: 'medium' }
    ];

    // Fonction pour exporter les données
    const exportData = (format) => {
        setLoading(true);
        setTimeout(() => {
            alert(`Export ${format} démarré. Vous recevrez un email avec le lien de téléchargement.`);
            setLoading(false);
        }, 1000);
    };

    return (
        <div className={`app-container ${darkMode ? 'dark' : ''}`}>
            {/* Navbar */}
            <nav className="navbar">
                <div className="nav-container">
                    <div className="nav-logo">
                        <div>
                            <div className="logo-text">INSData API</div>
                            <div className="logo-subtitle">Tableau de bord</div>
                        </div>
                    </div>

                    <div className="nav-links">
                        <a href="/" className="nav-link">
                            <Code size={18} />
                            <span>Accueil</span>
                        </a>
                        <a href="/documentation" className="nav-link">
                            <FileText size={18} />
                            <span>Documentation</span>
                        </a>
                        <a href="/dashboard" className="nav-link active">
                            <BarChartIcon size={18} />
                            <span>Tableau de bord</span>
                        </a>
                    </div>

                    <div className="nav-actions">
                        <button className="nav-button">
                            <Calendar size={20} />
                        </button>
                        <button
                            onClick={() => setDarkMode(!darkMode)}
                            className="nav-button"
                        >
                            {darkMode ? <Sun size={20} /> : <Moon size={20} />}
                        </button>
                        <button className="login-button">
                            Admin
                        </button>
                        <button
                            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                            className="mobile-menu-button"
                        >
                            {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
                        </button>
                    </div>
                </div>

                {mobileMenuOpen && (
                    <div className="mobile-menu">
                        <a href="/" className="mobile-nav-link">
                            <Code size={18} />
                            <span>Accueil</span>
                        </a>
                        <a href="/documentation" className="mobile-nav-link">
                            <FileText size={18} />
                            <span>Documentation</span>
                        </a>
                        <a href="/dashboard" className="mobile-nav-link">
                            <BarChartIcon size={18} />
                            <span>Tableau de bord</span>
                        </a>
                    </div>
                )}
            </nav>

            {/* Main Content */}
            <div className="main-content">
                {/* En-tête du dashboard */}
                <div className="dashboard-header">
                    <div className="dashboard-title-section">
                        <h1 className="section-title">Tableau de bord analytique</h1>
                        <p className="section-description">
                            Suivi en temps réel de l'utilisation des APIs et analyse de l'impact
                        </p>
                    </div>

                    <div className="dashboard-controls">
                        <div className="time-range-selector">
                            <Calendar size={18} />
                            <select value={timeRange} onChange={(e) => setTimeRange(e.target.value)}>
                                <option value="7d">7 derniers jours</option>
                                <option value="30d">30 derniers jours</option>
                                <option value="90d">90 derniers jours</option>
                                <option value="1y">1 an</option>
                            </select>
                        </div>

                        <div className="dashboard-tabs">
                            {['overview', 'users', 'apis', 'geography', 'impact'].map((tab) => (
                                <button
                                    key={tab}
                                    onClick={() => setActiveTab(tab)}
                                    className={`dashboard-tab ${activeTab === tab ? 'active' : ''}`}
                                >
                                    {tab === 'overview' && 'Vue générale'}
                                    {tab === 'users' && 'Utilisateurs'}
                                    {tab === 'apis' && 'APIs'}
                                    {tab === 'geography' && 'Géographie'}
                                    {tab === 'impact' && 'Impact'}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Statistiques principales */}
                <div className="stats-grid">
                    {overviewStats.map((stat, index) => (
                        <div key={index} className="dashboard-stat-card">
                            <div className="stat-icon-wrapper" style={{ background: `${stat.color}20` }}>
                                <stat.icon size={24} style={{ color: stat.color }} />
                            </div>
                            <div className="stat-content">
                                <div className="stat-label">{stat.label}</div>
                                <div className="stat-value">{stat.value}</div>
                                <div className={`stat-change ${stat.change.startsWith('+') ? 'positive' : 'negative'}`}>
                                    {stat.change.startsWith('+') ? <ArrowUp size={14} /> : <ArrowDown size={14} />}
                                    {stat.change}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Graphiques principaux */}
                <div className="dashboard-charts">
                    {/* Graphique des requêtes */}
                    <div className="chart-card">
                        <div className="chart-header">
                            <h3>Évolution des requêtes</h3>
                            <div className="chart-legend">
                                <div className="legend-item">
                                    <div className="legend-color" style={{ background: '#3b82f6' }}></div>
                                    <span>Requêtes</span>
                                </div>
                                <div className="legend-item">
                                    <div className="legend-color" style={{ background: '#ef4444' }}></div>
                                    <span>Erreurs</span>
                                </div>
                            </div>
                        </div>
                        <ResponsiveContainer width="100%" height={300}>
                            <AreaChart data={timeSeriesData[timeRange]}>
                                <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
                                <XAxis dataKey="date" />
                                <YAxis />
                                <Tooltip />
                                <Area type="monotone" dataKey="requests" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.1} />
                                <Area type="monotone" dataKey="errors" stroke="#ef4444" fill="#ef4444" fillOpacity={0.1} />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>

                    {/* Utilisation par API */}
                    <div className="chart-card">
                        <div className="chart-header">
                            <h3>Répartition par API</h3>
                            <PieChartIcon size={20} />
                        </div>
                        <ResponsiveContainer width="100%" height={300}>
                            <PieChart>
                                <Pie
                                    data={apiUsageData}
                                    cx="50%"
                                    cy="50%"
                                    labelLine={false}
                                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
                                    outerRadius={80}
                                    fill="#8884d8"
                                    dataKey="value"
                                >
                                    {apiUsageData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={entry.color} />
                                    ))}
                                </Pie>
                                <Tooltip />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Top utilisateurs et données géographiques */}
                <div className="dashboard-grid">
                    {/* Top utilisateurs */}
                    <div className="dashboard-card">
                        <div className="card-header">
                            <h3>Top 8 utilisateurs</h3>
                            <Users size={20} />
                        </div>
                        <div className="users-table">
                            <table>
                                <thead>
                                <tr>
                                    <th>Rang</th>
                                    <th>Organisation</th>
                                    <th>Requêtes</th>
                                    <th>APIs</th>
                                    <th>Localisation</th>
                                </tr>
                                </thead>
                                <tbody>
                                {topUsers.map((user) => (
                                    <tr key={user.rank} onClick={() => setSelectedRegion(user.location)}>
                                        <td>
                                            <div className={`rank-badge rank-${user.rank}`}>
                                                {user.rank}
                                            </div>
                                        </td>
                                        <td>
                                            <div className="user-info">
                                                <div className="user-name">{user.name}</div>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="stat-number">
                                                {user.requests.toLocaleString()}
                                            </div>
                                        </td>
                                        <td>{user.apis}</td>
                                        <td>
                                            <div className="location">
                                                <Map size={14} />
                                                {user.location}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Données géographiques */}
                    <div className="dashboard-card">
                        <div className="card-header">
                            <h3>Répartition géographique</h3>
                            <Globe size={20} />
                        </div>
                        <ResponsiveContainer width="100%" height={300}>
                            <BarChart data={geographicData}>
                                <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
                                <XAxis dataKey="region" />
                                <YAxis />
                                <Tooltip />
                                <Bar dataKey="requests" fill="#3b82f6" />
                                <Bar dataKey="users" fill="#10b981" />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Données d'adoption et types d'applications */}
                <div className="dashboard-grid">
                    {/* Tendance d'adoption */}
                    <div className="dashboard-card">
                        <div className="card-header">
                            <h3>Tendance d'adoption</h3>
                            <TrendingUp size={20} />
                        </div>
                        <ResponsiveContainer width="100%" height={300}>
                            <LineChart data={adoptionData}>
                                <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
                                <XAxis dataKey="month" />
                                <YAxis yAxisId="left" />
                                <YAxis yAxisId="right" orientation="right" />
                                <Tooltip />
                                <Legend />
                                <Line yAxisId="left" type="monotone" dataKey="newUsers" stroke="#3b82f6" name="Nouveaux utilisateurs" />
                                <Line yAxisId="right" type="monotone" dataKey="activeUsers" stroke="#10b981" name="Utilisateurs actifs" />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>

                    {/* Types d'applications */}
                    <div className="dashboard-card">
                        <div className="card-header">
                            <h3>Types d'applications</h3>
                            <Smartphone size={20} />
                        </div>
                        <ResponsiveContainer width="100%" height={300}>
                            <RadarChart cx="50%" cy="50%" outerRadius="80%" data={appTypesData}>
                                <PolarGrid />
                                <PolarAngleAxis dataKey="type" />
                                <PolarRadiusAxis />
                                <Radar name="Usage" dataKey="value" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.6} />
                            </RadarChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Alertes et impact */}
                <div className="dashboard-grid">
                    {/* Alertes */}
                    <div className="dashboard-card">
                        <div className="card-header">
                            <h3>Alertes système</h3>
                            <AlertCircle size={20} />
                        </div>
                        <div className="alerts-list">
                            {alerts.map((alert, index) => (
                                <div key={index} className={`alert-item alert-${alert.type} ${alert.priority}`}>
                                    <div className="alert-icon">
                                        {alert.type === 'warning' && <AlertCircle size={16} />}
                                        {alert.type === 'info' && <Info size={16} />}
                                        {alert.type === 'success' && <Check size={16} />}
                                    </div>
                                    <div className="alert-content">
                                        <div className="alert-message">{alert.message}</div>
                                        <div className="alert-time">{alert.time}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Mesures d'impact */}
                    <div className="dashboard-card">
                        <div className="card-header">
                            <h3>Indicateurs d'impact</h3>
                            <Activity size={20} />
                        </div>
                        <div className="impact-metrics">
                            <div className="impact-metric">
                                <div className="metric-label">Publications académiques</div>
                                <div className="metric-value">47</div>
                                <div className="metric-description">Utilisant nos données</div>
                            </div>
                            <div className="impact-metric">
                                <div className="metric-label">Décisions politiques</div>
                                <div className="metric-value">12</div>
                                <div className="metric-description">Influencées par nos APIs</div>
                            </div>
                            <div className="impact-metric">
                                <div className="metric-label">Startups créées</div>
                                <div className="metric-value">8</div>
                                <div className="metric-description">Basées sur notre plateforme</div>
                            </div>
                            <div className="impact-metric">
                                <div className="metric-label">ROI estimé</div>
                                <div className="metric-value">3.2x</div>
                                <div className="metric-description">Retour sur investissement</div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Actions d'export */}
                <div className="dashboard-actions">
                    <h3>Export des données analytiques</h3>
                    <div className="export-buttons">
                        <button onClick={() => exportData('PDF')} className="export-button">
                            <FileText size={18} />
                            <span>Rapport PDF</span>
                        </button>
                        <button onClick={() => exportData('Excel')} className="export-button">
                            <Database size={18} />
                            <span>Excel</span>
                        </button>
                        <button onClick={() => exportData('CSV')} className="export-button">
                            <DownloadCloud size={18} />
                            <span>CSV complet</span>
                        </button>
                        <button onClick={() => exportData('JSON')} className="export-button">
                            <Code size={18} />
                            <span>JSON API</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* Footer */}
            <footer className="footer">
                <div className="footer-content">
                    <div className="footer-column">
                        <h3>Tableau de bord</h3>
                        <p>
                            Données mises à jour en temps réel. Dernière actualisation : {new Date().toLocaleString('fr-FR')}
                        </p>
                    </div>
                    <div className="footer-column">
                        <h3>Métriques</h3>
                        <ul>
                            <li><a href="#">Performances API</a></li>
                            <li><a href="#">Utilisateurs actifs</a></li>
                            <li><a href="#">Impact économique</a></li>
                            <li><a href="#">Tendances d'usage</a></li>
                        </ul>
                    </div>
                    <div className="footer-column">
                        <h3>Rapports</h3>
                        <ul>
                            <li><a href="#">Mensuel</a></li>
                            <li><a href="#">Trimestriel</a></li>
                            <li><a href="#">Annuel</a></li>
                            <li><a href="#">Personnalisé</a></li>
                        </ul>
                    </div>
                    <div className="footer-column">
                        <h3>Administration</h3>
                        <ul>
                            <li><a href="#">Configurer les alertes</a></li>
                            <li><a href="#">Gérer les accès</a></li>
                            <li><a href="#">Audit des logs</a></li>
                            <li><a href="#">Support technique</a></li>
                        </ul>
                    </div>
                </div>
                <div className="footer-bottom">
                    <p>© 2025 INSData API By ADE- Tableau de bord analytique v1.0 | Données confidentielles - Usage interne uniquement</p>
                </div>
            </footer>
        </div>
    );
}

export default Dashboard;